import axios from 'axios';

const getDefaultApiBaseUrl = (envUrl) => {
  if (envUrl) return envUrl;
  
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // If running on localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8005/api';
    }
    
    // If running on network IP (192.168.x.x or similar)
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
      return `http://${hostname}:8005/api`;
    }
  }
  
  return 'https://api.klinnovac.org/api';
};

// Frontend Public API (read-only, no auth needed)
const PUBLIC_API = axios.create({
  baseURL: getDefaultApiBaseUrl(process.env.REACT_APP_PUBLIC_API_URL),
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

// Backend CMS API (requires auth)
const CMS_API = axios.create({
  baseURL: getDefaultApiBaseUrl(process.env.REACT_APP_CMS_API_URL),
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

// Attach token to CMS API requests
CMS_API.interceptors.request.use((config) => {
  const token = localStorage.getItem('cms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
CMS_API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cms_token');
      localStorage.removeItem('cms_user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// ========== PUBLIC API (Frontend Website) ==========
export const publicApi = {
  getSettings: () => PUBLIC_API.get('/settings'),
  getPages: (slug) => PUBLIC_API.get(`/pages${slug ? `?slug=${slug}` : ''}`),
  getPage: (slug) => PUBLIC_API.get(`/pages/${slug}`),
  getPosts: (params) => PUBLIC_API.get('/posts', { params }),
  getPost: (slug) => PUBLIC_API.get(`/posts/${slug}`),
  getSections: (params) => PUBLIC_API.get('/sections', { params }),
  ping: () => PUBLIC_API.get('/ping'),
};

// ========== CMS API (Backend Admin) ==========
// Auth
export const authApi = {
  login: (email, password) => CMS_API.post('/login', { email, password }),
  logout: () => CMS_API.post('/logout'),
  me: () => CMS_API.get('/me'),
  register: (data) => CMS_API.post('/register', data),
};

// Users
export const userApi = {
  list: (params) => CMS_API.get('/users', { params }),
  get: (id) => CMS_API.get(`/users/${id}`),
  create: (data) => CMS_API.post('/users', data),
  update: (id, data) => CMS_API.put(`/users/${id}`, data),
  delete: (id) => CMS_API.delete(`/users/${id}`),
};

// Roles
export const roleApi = {
  list: () => CMS_API.get('/roles'),
  get: (id) => CMS_API.get(`/roles/${id}`),
  create: (data) => CMS_API.post('/roles', data),
  update: (id, data) => CMS_API.put(`/roles/${id}`, data),
  delete: (id) => CMS_API.delete(`/roles/${id}`),
};

// Posts (Blog)
export const postApi = {
  list: (params) => CMS_API.get('/posts', { params }),
  get: (id) => CMS_API.get(`/posts/${id}`),
  create: (data) => CMS_API.post('/posts', data),
  update: (id, data) => CMS_API.put(`/posts/${id}`, data),
  delete: (id) => CMS_API.delete(`/posts/${id}`),
};

// Pages
export const pageApi = {
  list: (params) => CMS_API.get('/pages', { params }),
  get: (id) => CMS_API.get(`/pages/${id}`),
  create: (data) => CMS_API.post('/pages', data),
  update: (id, data) => CMS_API.put(`/pages/${id}`, data),
  delete: (id) => CMS_API.delete(`/pages/${id}`),
};

// Sections
export const sectionApi = {
  list: (params) => CMS_API.get('/sections', { params }),
  get: (id) => CMS_API.get(`/sections/${id}`),
  create: (data) => CMS_API.post('/sections', data),
  update: (id, data) => CMS_API.put(`/sections/${id}`, data),
  delete: (id) => CMS_API.delete(`/sections/${id}`),
};

// Media
export const mediaApi = {
  list: (params) => CMS_API.get('/media', { params }),
  upload: (formData) => CMS_API.post('/media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => CMS_API.delete(`/media/${id}`),
};

// Settings
export const settingApi = {
  list: (params) => CMS_API.get('/settings', { params }),
  update: (data) => CMS_API.put('/settings', { settings: data }),
};

// ========== POS API ==========
export const posApi = {
  categories: {
    list: (params) => CMS_API.get('/service-categories', { params }),
    get: (id) => CMS_API.get(`/service-categories/${id}`),
    create: (data) => CMS_API.post('/service-categories', data),
    update: (id, data) => CMS_API.put(`/service-categories/${id}`, data),
    delete: (id) => CMS_API.delete(`/service-categories/${id}`),
  },
  items: {
    list: (params) => CMS_API.get('/service-items', { params }),
    get: (id) => CMS_API.get(`/service-items/${id}`),
    create: (data) => CMS_API.post('/service-items', data),
    update: (id, data) => CMS_API.put(`/service-items/${id}`, data),
    delete: (id) => CMS_API.delete(`/service-items/${id}`),
  },
  packages: {
    list: (params) => CMS_API.get('/service-packages', { params }),
    get: (id) => CMS_API.get(`/service-packages/${id}`),
    create: (data) => CMS_API.post('/service-packages', data),
    update: (id, data) => CMS_API.put(`/service-packages/${id}`, data),
    delete: (id) => CMS_API.delete(`/service-packages/${id}`),
  },
  orders: {
    list: (params) => CMS_API.get('/orders', { params }),
    get: (id) => CMS_API.get(`/orders/${id}`),
    create: (data) => CMS_API.post('/orders', data),
    update: (id, data) => CMS_API.put(`/orders/${id}`, data),
    delete: (id) => CMS_API.delete(`/orders/${id}`),
  },
};

// ========== OPERATION API ==========
export const operationApi = {
  inventoryItems: {
    list: (params) => CMS_API.get('/admin/operation/inventory-items', { params }),
    get: (id) => CMS_API.get(`/admin/operation/inventory-items/${id}`),
    create: (data) => CMS_API.post('/admin/operation/inventory-items', data),
    update: (id, data) => CMS_API.put(`/admin/operation/inventory-items/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/inventory-items/${id}`),
  },
  profiles: {
    list: (params) => CMS_API.get('/admin/operation/profiles', { params }),
    get: (id) => CMS_API.get(`/admin/operation/profiles/${id}`),
    create: (data) => CMS_API.post('/admin/operation/profiles', data),
    update: (id, data) => CMS_API.put(`/admin/operation/profiles/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/profiles/${id}`),
  },
  assignments: {
    list: (params) => CMS_API.get('/admin/operation/assignments', { params }),
    get: (id) => CMS_API.get(`/admin/operation/assignments/${id}`),
    create: (data) => CMS_API.post('/admin/operation/assignments', data),
    update: (id, data) => CMS_API.put(`/admin/operation/assignments/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/assignments/${id}`),
  },
  profilePrices: {
    get: (profileId) => CMS_API.get(`/admin/operation/profiles/${profileId}/prices`),
    setPackagePrice: (profileId, data) => CMS_API.post(`/admin/operation/profiles/${profileId}/package-prices`, data),
    deletePackagePrice: (profileId, priceId) => CMS_API.delete(`/admin/operation/profiles/${profileId}/package-prices/${priceId}`),
    setItemPrice: (profileId, data) => CMS_API.post(`/admin/operation/profiles/${profileId}/item-prices`, data),
    deleteItemPrice: (profileId, priceId) => CMS_API.delete(`/admin/operation/profiles/${profileId}/item-prices/${priceId}`),
  },
  profileAssignments: {
    getAssigned: (profileId) => CMS_API.get(`/admin/operation/profiles/${profileId}/assigned-services`),
    assignItems: (profileId, data) => CMS_API.post(`/admin/operation/profiles/${profileId}/assign-items`, data),
    assignPackages: (profileId, data) => CMS_API.post(`/admin/operation/profiles/${profileId}/assign-packages`, data),
  },
  inventoryCategories: {
    list: (params) => CMS_API.get('/admin/operation/inventory-categories', { params }),
    get: (id) => CMS_API.get(`/admin/operation/inventory-categories/${id}`),
    create: (data) => CMS_API.post('/admin/operation/inventory-categories', data),
    update: (id, data) => CMS_API.put(`/admin/operation/inventory-categories/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/inventory-categories/${id}`),
  },
  positions: {
    list: (params) => CMS_API.get('/admin/operation/positions', { params }),
    get: (id) => CMS_API.get(`/admin/operation/positions/${id}`),
    create: (data) => CMS_API.post('/admin/operation/positions', data),
    update: (id, data) => CMS_API.put(`/admin/operation/positions/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/positions/${id}`),
  },
  departments: {
    list: (params) => CMS_API.get('/admin/operation/departments', { params }),
    get: (id) => CMS_API.get(`/admin/operation/departments/${id}`),
    create: (data) => CMS_API.post('/admin/operation/departments', data),
    update: (id, data) => CMS_API.put(`/admin/operation/departments/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/departments/${id}`),
  },
  inventoryCategories: {
    list: (params) => CMS_API.get('/admin/operation/inventory-categories', { params }),
    get: (id) => CMS_API.get(`/admin/operation/inventory-categories/${id}`),
    create: (data) => CMS_API.post('/admin/operation/inventory-categories', data),
    update: (id, data) => CMS_API.put(`/admin/operation/inventory-categories/${id}`, data),
    delete: (id) => CMS_API.delete(`/admin/operation/inventory-categories/${id}`),
  },
  exportInventoryItems: (params) => CMS_API.get('/admin/operation/export/inventory-items', { params }),
  exportAssignments: (params) => CMS_API.get('/admin/operation/export/assignments', { params }),
  exportProfiles: (params) => CMS_API.get('/admin/operation/export/profiles', { params }),
  packages: (params) => CMS_API.get('/service-packages', { params: { ...params, per_page: 100 } }),
  serviceItems: (params) => CMS_API.get('/service-items', { params: { ...params, per_page: 100 } }),
};

// Public POS API (no auth needed for customer ordering)
const PUBLIC_POS_API = axios.create({
  baseURL: getDefaultApiBaseUrl(process.env.REACT_APP_CMS_API_URL),
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

export const publicPosApi = {
  getCategories: (params) => PUBLIC_POS_API.get('/public/service-categories', { params }),
  getItems: (params) => PUBLIC_POS_API.get('/public/service-items', { params }),
  getPackages: (params) => PUBLIC_POS_API.get('/public/service-packages', { params }),
  submitOrder: (data) => PUBLIC_POS_API.post('/public/orders', data),
};

// Token management helpers
export const tokenManager = {
  set: (token, user) => {
    localStorage.setItem('cms_token', token);
    localStorage.setItem('cms_user', JSON.stringify(user));
  },
  get: () => ({
    token: localStorage.getItem('cms_token'),
    user: JSON.parse(localStorage.getItem('cms_user') || 'null'),
  }),
  clear: () => {
    localStorage.removeItem('cms_token');
    localStorage.removeItem('cms_user');
  },
  isAuthenticated: () => !!localStorage.getItem('cms_token'),
};